import { useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { clientsApi, salesApi } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SalesCalendar } from '@/components/charts/sales-calendar';

type ClientOption = {
  id: string;
  name: string;
  cif: string;
};

type Sale = {
  id: string;
  amount: number | string;
  saleDate: string;
  notes?: string;
  createdAt: string;
  client: ClientOption;
};

type SaleEditForm = {
  clientId: string;
  amount: string;
  saleDate: string;
  notes: string;
};

type UpdateSalePayload = {
  clientId: string;
  amount: number;
  saleDate: string;
  notes?: string;
};

export function SalesPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [clientFilter, setClientFilter] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [searchText, setSearchText] = useState('');
  const [editingSaleId, setEditingSaleId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<SaleEditForm>({
    clientId: '',
    amount: '',
    saleDate: '',
    notes: '',
  });
  const limit = 10;

  const monthRange = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [selectedMonth]);

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-sales'],
    queryFn: () => clientsApi.getAll({ page: 1, limit: 100, sortBy: 'name', sortOrder: 'ASC' }),
  });

  const clients: ClientOption[] = clientsData?.data.data || [];

  const { data: salesData, isLoading } = useQuery({
    queryKey: ['sales', page, clientFilter, monthRange.startDate, monthRange.endDate],
    queryFn: () =>
      salesApi.getAll({
        page,
        limit,
        clientId: clientFilter || undefined,
        startDate: monthRange.startDate,
        endDate: monthRange.endDate,
        sortBy: 'saleDate',
        sortOrder: 'DESC',
      }),
  });

  const total = salesData?.data.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const sales = useMemo(() => {
    const rawSales: Sale[] = salesData?.data.data ?? [];
    const query = searchText.trim().toLowerCase();

    if (!query) return rawSales;

    return rawSales.filter((sale) => {
      const clientName = sale.client?.name?.toLowerCase() || '';
      const clientCif = sale.client?.cif?.toLowerCase() || '';
      const notes = sale.notes?.toLowerCase() || '';
      return clientName.includes(query) || clientCif.includes(query) || notes.includes(query);
    });
  }, [salesData?.data.data, searchText]);

  const updateSaleMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSalePayload }) => salesApi.update(id, payload),
    onSuccess: () => {
      toast.success('Venta actualizada');
      setEditingSaleId(null);
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['sales-stats'] });
      queryClient.invalidateQueries({ queryKey: ['sales-current-month-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-month-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['user-target-current'] });
      queryClient.invalidateQueries({ queryKey: ['sales-recent-new-sale'] });
      queryClient.invalidateQueries({ queryKey: ['sales-month-list-new-sale'] });
    },
  });

  const deleteSaleMutation = useMutation({
    mutationFn: (id: string) => salesApi.delete(id),
    onSuccess: () => {
      toast.success('Venta eliminada');
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['sales-stats'] });
      queryClient.invalidateQueries({ queryKey: ['sales-current-month-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['sales-target-month-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['user-target-current'] });
      queryClient.invalidateQueries({ queryKey: ['sales-recent-new-sale'] });
      queryClient.invalidateQueries({ queryKey: ['sales-month-list-new-sale'] });
    },
  });

  const startEdit = (sale: Sale) => {
    setEditingSaleId(sale.id);
    setEditForm({
      clientId: sale.client?.id || '',
      amount: String(sale.amount),
      saleDate: String(sale.saleDate).slice(0, 10),
      notes: sale.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditingSaleId(null);
    setEditForm({
      clientId: '',
      amount: '',
      saleDate: '',
      notes: '',
    });
  };

  const saveEdit = async (saleId: string) => {
    const amount = Number(editForm.amount);
    if (!editForm.clientId) {
      toast.error('Selecciona un cliente');
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('El importe debe ser mayor que 0');
      return;
    }

    if (!editForm.saleDate) {
      toast.error('La fecha es obligatoria');
      return;
    }

    await updateSaleMutation.mutateAsync({
      id: saleId,
      payload: {
        clientId: editForm.clientId,
        amount,
        saleDate: editForm.saleDate,
        notes: editForm.notes.trim() || undefined,
      },
    });
  };

  const deleteSale = async (saleId: string) => {
    if (!window.confirm('¿Eliminar esta venta?')) return;
    await deleteSaleMutation.mutateAsync(saleId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Ventas</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Listado y edición de ventas.
        </p>
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-2">
        <SalesCalendar displayDate={selectedMonth} onDisplayDateChange={(next) => { setSelectedMonth(next); setPage(1); }} />

        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Listado ({total})</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Cliente</label>
              <select
                value={clientFilter}
                onChange={(event) => {
                  setClientFilter(event.target.value);
                  setPage(1);
                }}
                className="h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Todos</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Buscar</label>
              <Input
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Cliente, CIF o notas"
              />
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-gray-500">Cargando ventas...</p>
          ) : sales.length === 0 ? (
            <p className="text-sm text-gray-500">No hay ventas para mostrar.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 text-left">
                    <th className="py-2 pr-4">Cliente</th>
                    <th className="py-2 pr-4">Importe</th>
                    <th className="py-2 pr-4">Fecha</th>
                    <th className="py-2 pr-4">Notas</th>
                    <th className="py-2 pr-4">Creado</th>
                    <th className="py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 pr-4">
                        {editingSaleId === sale.id ? (
                          <select
                            value={editForm.clientId}
                            onChange={(event) =>
                              setEditForm((prev) => ({ ...prev, clientId: event.target.value }))
                            }
                            className="h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="">Seleccionar cliente</option>
                            {clients.map((client) => (
                              <option key={client.id} value={client.id}>
                                {client.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <p className="font-medium">{sale.client?.name || '-'}</p>
                            <p className="text-xs text-gray-500">{sale.client?.cif || '-'}</p>
                          </>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {editingSaleId === sale.id ? (
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={editForm.amount}
                            onChange={(event) =>
                              setEditForm((prev) => ({ ...prev, amount: event.target.value }))
                            }
                          />
                        ) : (
                          formatCurrency(Number(sale.amount))
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {editingSaleId === sale.id ? (
                          <Input
                            type="date"
                            value={editForm.saleDate}
                            onChange={(event) =>
                              setEditForm((prev) => ({ ...prev, saleDate: event.target.value }))
                            }
                          />
                        ) : (
                          formatDate(sale.saleDate)
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {editingSaleId === sale.id ? (
                          <Input
                            value={editForm.notes}
                            onChange={(event) =>
                              setEditForm((prev) => ({ ...prev, notes: event.target.value }))
                            }
                            placeholder="Sin notas"
                          />
                        ) : (
                          sale.notes || '-'
                        )}
                      </td>
                      <td className="py-3 pr-4">{formatDate(sale.createdAt)}</td>
                      <td className="py-3">
                        {editingSaleId === sale.id ? (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveEdit(sale.id)}
                              loading={updateSaleMutation.isPending}
                            >
                              Guardar
                            </Button>
                            <Button size="sm" variant="outline" onClick={cancelEdit}>
                              Cancelar
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => startEdit(sale)}>
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              loading={deleteSaleMutation.isPending}
                              onClick={() => deleteSale(sale.id)}
                            >
                              Borrar
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Página {page} de {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Siguiente
              </Button>
            </div>
          </div>

          <section className="bg-gray-50 dark:bg-gray-950 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Total mostrado: {formatCurrency(sales.reduce((acc, sale) => acc + Number(sale.amount), 0))}
            </p>
          </section>
        </section>
      </div>
    </div>
  );
}
